import { z } from "zod";
import { RiskRanks } from "../../risk/Risk";

const StakeholderCategoryEnum = z.enum(["people", "organisation", "ecosystem"]);

const ImpactPlusHarmSchema = z.object({
  harmRank: z.enum(RiskRanks).default(RiskRanks[0]),
  description: z.string().default(""),
});

const ImpactPlusCohortSchema = z.object({
  stakeholderCategory: StakeholderCategoryEnum.default("people"),
  label: z.string().default(""),
  description: z.string().default(""),
  harms: z.array(ImpactPlusHarmSchema).default([]),
  cohortRisk: z.enum(RiskRanks).default(RiskRanks[0]),
  harmSummary: z.string().default(""),
  harmJustification: z.string().default(""),
});

const ImpactPlusCohortRankingSchema = z.object({
  riskRankLabel: z.enum(RiskRanks),
  riskRankNum: z.number(),
  numCohorts: z.number().default(0),
});

const ImpactPlusTitlePageSchema = z.object({
  title: z.string().default(""),
  subtitle: z.string().default(""),
  useCaseTitle: z.string().default(""),
  client: z.string().default(""),
  reportDate: z.string().default(""),
  riskAssessmentStage: z.string().default(""),
});

const defaultTitlePage = {
  title: "",
  subtitle: "",
  useCaseTitle: "",
  client: "",
  reportDate: "",
  riskAssessmentStage: "",
} as const;

const ImpactPlusDecisionGuidanceSchema = z.object({
  verdictLevel: z.number().default(0),
  verdictLabel: z.string().default(""),
  verdict: z.string().default(""),
  cohortStatsSummary: z.string().default(""),
  cohortRankings: z.array(ImpactPlusCohortRankingSchema).default([]),
  executiveBullets: z.array(z.string()).default([]),
});

const defaultDecisionGuidance = {
  verdictLevel: 0,
  verdictLabel: "",
  verdict: "",
  cohortStatsSummary: "",
  cohortRankings: [] as z.infer<typeof ImpactPlusCohortRankingSchema>[],
  executiveBullets: [] as string[],
};

const ImpactPlusImpactedStakeholdersSchema = z.object({
  heading: z.string().default(""),
  introduction: z.string().default(""),
  impactedCohorts: z.array(ImpactPlusCohortSchema).default([]),
});

const defaultImpactedStakeholders = {
  heading: "",
  introduction: "",
  impactedCohorts: [] as z.infer<typeof ImpactPlusCohortSchema>[],
};

const ImpactPlusNextStepsSchema = z.object({
  heading: z.string().default(""),
  introduction: z.string().default(""),
  recommendedNextStep: z.string().default(""),
});

const defaultNextSteps = {
  heading: "",
  introduction: "",
  recommendedNextStep: "",
} as const;

const defaultHarm = { harmRank: RiskRanks[0], description: "" } as const;

const defaultInternalImpact = {
  financial: defaultHarm,
  reputational: defaultHarm,
  operational: defaultHarm,
  legal: defaultHarm,
} as const;

const defaultExternalImpact = {
  people: defaultHarm,
  organisations: defaultHarm,
  ecosystems: defaultHarm,
} as const;

const ImpactPlusInternalImpactSchema = z.object({
  financial: ImpactPlusHarmSchema.default(defaultHarm),
  reputational: ImpactPlusHarmSchema.default(defaultHarm),
  operational: ImpactPlusHarmSchema.default(defaultHarm),
  legal: ImpactPlusHarmSchema.default(defaultHarm),
});

const ImpactPlusExternalImpactSchema = z.object({
  people: ImpactPlusHarmSchema.default(defaultHarm),
  organisations: ImpactPlusHarmSchema.default(defaultHarm),
  ecosystems: ImpactPlusHarmSchema.default(defaultHarm),
});

const ImpactPlusAISystemImpactSchema = z.object({
  internal: ImpactPlusInternalImpactSchema.default(defaultInternalImpact),
  external: ImpactPlusExternalImpactSchema.default(defaultExternalImpact),
});

const ImpactPlusAISystemImpactSummarySchema = z.object({
  internalImpactSummary: z.string().default(""),
  externalImpactSummary: z.string().default(""),
  riskConcentrationSummary: z.string().default(""),
});

const defaultAISystemImpactSummary = {
  internalImpactSummary: "",
  externalImpactSummary: "",
  riskConcentrationSummary: "",
} as const;

const ImpactPlusDependencySchema = z.object({
  ref_useCaseChat: z.string().optional(),
  ref_riskProfile: z.string().optional(),
  contentVersion: z.number().default(0),
});

export const ImpactPlusReportSchema = z.object({
  id: z.string().default(""),
  useCaseChatId: z.string().default(""),
  riskProfileId: z.string().default(""),
  generatedByUserId: z.string().default(""),
  titlePage: ImpactPlusTitlePageSchema.default(defaultTitlePage),
  decisionGuidance: ImpactPlusDecisionGuidanceSchema.default(defaultDecisionGuidance),
  impactedStakeholders: ImpactPlusImpactedStakeholdersSchema.default(defaultImpactedStakeholders),
  nextSteps: ImpactPlusNextStepsSchema.default(defaultNextSteps),
  overallRiskRank: z.enum(RiskRanks).default(RiskRanks[0]),
  aiSystemImpact: ImpactPlusAISystemImpactSchema.default({
    internal: defaultInternalImpact,
    external: defaultExternalImpact,
  }),
  aiSystemImpactSummary: ImpactPlusAISystemImpactSummarySchema.default(defaultAISystemImpactSummary),
  contentVersion: z.number().default(0),
  dependencies: z.array(ImpactPlusDependencySchema).default([]),
  isStale: z.boolean().default(false),
});

export type ImpactPlusReport = z.infer<typeof ImpactPlusReportSchema>;
export type ImpactPlusCohort = z.infer<typeof ImpactPlusCohortSchema>;
export type ImpactPlusHarm = z.infer<typeof ImpactPlusHarmSchema>;
export type ImpactPlusCohortRanking = z.infer<typeof ImpactPlusCohortRankingSchema>;
export type StakeholderCategory = z.infer<typeof StakeholderCategoryEnum>;
